import React, { useContext, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";

import { Breadcrumbs, Divider, IconButton, Link, Menu, MenuItem, Skeleton, Stack, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AppContext } from "App";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";

import { usePageTitle } from "../../hooks/usePageTitle";
import { BreadCrumbRightArrowIcon, MenuIcon, MoreIconFilled, RedoIcon } from "../Icons/Icons";
import Letterhead from "../Letterhead";
import { NAVIGATION_MENU_DRAWER_WIDTH, NAVIGATION_MENU_DRAWER_WIDTH_COLLAPSED } from "../Menu/config/constants";

const MENU_BUTTON_WITH_MARGIN_WIDTH = 60;

export default function WorkspaceHeader({
  title,
  subtitle = null,
  category = {},
  breadCrumbs = [],
  isLoading = false,
  compact = false,
}) {
  const { globalState, globalDispatch } = useContext(AppContext);

  const titleStr = React.isValidElement(title) ? "Tilley. How farms grow money" : String(title || "");
  usePageTitle(titleStr);

  const handleMenuButtonClick = () => {
    globalDispatch({
      type: "update",
      payload: {
        key: "isMenuOpen",
        value: true,
      },
    });
  };

  const calculateDisplayTitle = useMemo(() => {
    if (typeof title === "string" && title.trim) {
      return title.trim().length > 0 ? title.trim() : <>&nbsp;</>;
    } else {
      return title;
    }
  }, [title]);

  const isLg = useMediaQuery((theme) => theme.breakpoints.up("lg"), { noSsr: true });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      className="workspace-header"
      sx={{
        mb: { xs: compact ? "0px" : "26px", sm: "16px", lg: "32px" },
      }}
    >
      <Box sx={{ display: "flex", pt: 2, alignItems: "flex-start" }}>
        <IconButton
          className="no-print"
          onClick={handleMenuButtonClick}
          sx={{ mr: 0, mt: "-8px", height: "40px", width: "40px", display: { xs: "block", md: "none" } }}
        >
          <MenuIcon />
        </IconButton>

        <Stack direction={"column"} justifyContent="center" sx={{ gap: { xs: "12px", md: "22px" } }}>
          {breadCrumbs.length > 0 ? (
            <>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                elevation={2}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: "var(--mui-palette-action-hover)",
                    borderRadius: "0.5rem",
                    border: "1px solid var(--mui-palette-action-focus)",
                  },
                }}
                aria-labelledby="more-breadcrumbs-menu"
              >
                {breadCrumbs.slice(0, -1).map((b, index) => [
                  <MenuItem key={`menu-item-${index}`} onClick={handleClose}>
                    <Link
                      underline="none"
                      color="text.primary"
                      sx={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "4px" }}
                      component={RouterLink}
                      to={b?.path}
                    >
                      <RedoIcon sx={{ width: "1rem", height: "1rem" }} />
                      {b?.title}
                    </Link>
                  </MenuItem>,
                  index < breadCrumbs.slice(0, -1).length - 1 && (
                    <Divider
                      key={`divider-${index}`}
                      sx={{
                        borderColor: "var(--mui-palette-grey-200)",
                        marginTop: "0px !important",
                        marginBottom: "0px !important",
                        marginLeft: "6px",
                        marginRight: "6px",
                      }}
                    />
                  ),
                ])}
              </Menu>
              <Breadcrumbs
                separator={<BreadCrumbRightArrowIcon color="text.secondary" sx={{ width: "12px", height: "12px" }} />}
                className="no-print"
                sx={{
                  width: [
                    `calc(100vw - ${MENU_BUTTON_WITH_MARGIN_WIDTH}px)`,
                    globalState?.isMenuOpen
                      ? `calc(100vw - ${MENU_BUTTON_WITH_MARGIN_WIDTH}px - ${NAVIGATION_MENU_DRAWER_WIDTH}px)`
                      : `calc(100vw - ${MENU_BUTTON_WITH_MARGIN_WIDTH}px - ${NAVIGATION_MENU_DRAWER_WIDTH_COLLAPSED}px)`,
                  ],
                }}
              >
                {isLg ? (
                  breadCrumbs.slice(0, -1).map((b, index) => {
                    const hasPath = Boolean(b?.path);
                    return hasPath ? (
                      <Link
                        key={index}
                        underline="none"
                        color="text.secondary"
                        sx={{ fontSize: "14px" }}
                        component={RouterLink}
                        to={b?.path}
                      >
                        {b?.title ? b?.title : <Skeleton variant="text" width={80} height={14} />}
                      </Link>
                    ) : (
                      <Typography color="text.secondary" sx={{ fontSize: "14px" }} key={index}>
                        {b?.title}
                      </Typography>
                    );
                  })
                ) : breadCrumbs.length > 1 ? (
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{ width: "1rem", height: "1rem", marginLeft: "1rem", padding: "0px" }}
                    onClick={handleClick}
                  >
                    <MoreIconFilled sx={{ width: "1rem", height: "1rem" }} />
                  </IconButton>
                ) : null}
                <Link
                  underline="none"
                  color="text.secondary"
                  sx={{ fontSize: "14px" }}
                  component={RouterLink}
                  to={breadCrumbs[breadCrumbs.length - 1]?.path}
                >
                  {breadCrumbs[breadCrumbs.length - 1]?.title ? (
                    breadCrumbs[breadCrumbs.length - 1]?.title
                  ) : (
                    <Skeleton variant="text" width={80} height={14} />
                  )}
                </Link>
                <Typography color="text.secondary"></Typography>
              </Breadcrumbs>
            </>
          ) : (
            !compact && (
              <Typography className="no-print" sx={{ visibility: "hidden" }}>
                &nbsp;
              </Typography>
            )
          )}
          <Box className="workspace-header-content">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <SkeletonWrapper variant="text" isLoading={isLoading || !calculateDisplayTitle}>
                <Typography variant="h5" color="primary.dark">
                  {calculateDisplayTitle}
                </Typography>
              </SkeletonWrapper>
              {subtitle && (
                <SkeletonWrapper variant="text" isLoading={isLoading || !subtitle}>
                  <Typography variant={"h5"} color="primary.dark">
                    {subtitle}
                  </Typography>
                </SkeletonWrapper>
              )}
              <Box className="category-block" sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {category?.icon || <Skeleton variant="circular" width={18} height={18} />}
                <SkeletonWrapper variant="text" isLoading={isLoading || !category?.title}>
                  <Typography variant="subtitle1">{category?.title}</Typography>
                </SkeletonWrapper>
              </Box>
            </Box>
            <Letterhead />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
